import React, { useCallback, useState } from "react";
import { css } from "@emotion/react";
import { useMutation } from "@apollo/react-hooks";
import { isEmpty } from "lodash";

import useForm, {
    FieldList,
    OnFieldChange,
    SetFormFunction,
    ValidateFunction,
    Validator
} from "core/hooks/useForm";
import { useAppState } from "core/contexts/AppContext";
import useToast from "core/hooks/useToast";
import { NOOP } from "core/constants";
import validationErrorParser from "core/includes/validationErrorParser";
import RoundButton from "core/Components/Buttons/RoundButton";

type Props = {
    label: string,
    inputs: Array<InputConfig>,
    mutation: any,
    onFinish?: () => void,
    validator?: Validator,
}

type Errors = {
    [key: string]: any,
}

const ChangeValue: React.FC<Props> = ({ label, inputs, mutation, onFinish = NOOP, validator = null }) => {
    const [, errorToast] = useToast();
    const [appState] = useAppState();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const initialFields: FieldList = {};

    for (const input of inputs) {
        initialFields[input.camelKey] = input.initialValue;
    }

    const defaultValidator = useCallback((fields: FieldList) => {
        const errors: Errors = {};

        for (const input of inputs) {
            if (!fields[input.camelKey]) {
                errors[input.camelKey] = `Please enter your ${input.label.toLowerCase()}.`;
            }
        }

        return errors;
    }, [inputs]);

    const _validator = validator ? validator : defaultValidator;

    const { fields, onFieldChange, validate, errors, setFormErrors }: {
        fields: FieldList,
        onFieldChange: OnFieldChange,
        validate: ValidateFunction,
        errors: FieldList,
        setFormErrors: SetFormFunction,
    } = useForm({
        fields: initialFields,
        validator: _validator,
    });

    const [changeValueMutation] = useMutation(mutation, {
        context: { clientName: "auth" },
    });

    const submitChangeValueForm = useCallback(() => {
        const valResult = validate();

        if (valResult) {
            setIsSubmitting(true);
            changeValueMutation({
                variables: {
                    ...fields,
                }
            }).then(() => {
                appState.refetchUser().then(() => {
                    setIsSubmitting(false);
                    onFinish();
                });
            }).catch((e: any) => {
                setIsSubmitting(false);

                const validationErrors: any = validationErrorParser(e)
                if (!isEmpty(validationErrors)) {
                    setFormErrors(validationErrors)
                }

                if (validationErrors["errorMessage"]) {
                    errorToast(validationErrors["errorMessage"])
                }
            });
        }
    }, [changeValueMutation, appState, fields, errorToast, setFormErrors, validate, onFinish]);

    return (
        <div>
            <h2 css={titleStyle}>Change {label}</h2>
            {!!Object.values(errors).length && (
                <div css={errorStyle}>{Object.values(errors)[0]}</div>
            )}
            {inputs.map((input) => (
                <div key={input.camelKey}>
                    <label>{input.label}</label>
                    <input
                        name={input.camelKey}
                        onChange={(e) => {
                            const value = input.type === "checkbox" ? e.target.checked : e.target.value;
                            onFieldChange(input.camelKey, value);
                        }}
                        value={fields[input.camelKey]}
                        type={input.type}
                    />
                </div>
            ))}
            <RoundButton onButtonClick={submitChangeValueForm} css={buttonStyle} forceLoading={isSubmitting}>
                Submit
            </RoundButton>
            <RoundButton onButtonClick={onFinish} css={buttonStyle}>
                Cancel
            </RoundButton>
        </div>
    );
}

const buttonStyle = css`
    min-width: 60px;
`;

const titleStyle = css`
    font-size: 18px;
`;

const errorStyle = css`
    color: red;
`;

export default ChangeValue;
